import { useState, useRef, useEffect } from "react";
import { Profile } from "../types";
import { ApiService } from "../../../../../config";

/**
 * Hook para gerenciar o formulário de edição de perfil.
 * Inicializa os dados do formulário, carrega categorias disponíveis e
 * fornece métodos para atualizar os valores dos campos.
 * 
 * @param profile - Dados do perfil a ser editado (null para novo perfil)
 * @param isOpen - Indica se o modal está aberto
 * @returns Objeto com dados do formulário, categorias e métodos de atualização
 */
export const useEditProfileForm = (profile: Profile | null, isOpen: boolean) => {
  const [formData, setFormData] = useState<Profile | null>(null);
  const [categories, setCategories] = useState<
    Array<{ gcid: string; category: string }>
  >([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const hasInitialized = useRef(false);

  /**
   * Inicializa os dados do formulário quando o modal é aberto.
   */
  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      if (profile) {
        setFormData({
          ...profile,
          secondaryCategories: profile.secondaryCategories || [],
          specialHours: profile.specialHours || [],
          amenities: profile.amenities || [],
          accessibility: profile.accessibility || [],
          parking: profile.parking || [],
          payments: profile.payments || [],
          serviceOptions: profile.serviceOptions || [],
        });
        hasInitialized.current = true;
      } else {
        setFormData({
          id: "1",
          name: "",
          category: "",
          status: "active",
          stats: {
            views: 0,
            clicks: 0,
            ranking: 0,
          },
        });
        hasInitialized.current = true;
      }
    } else if (!isOpen) {
      hasInitialized.current = false;
    }
  }, [profile, isOpen]);

  /**
   * Carrega lista de categorias disponíveis quando o modal é aberto.
   */
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await ApiService.get("/business-profiles/categories");
        if (response.data && response.data.data) {
          setCategories(response.data.data);
        }
      } catch {
        // Erro ao buscar categorias
      } finally {
        setLoadingCategories(false);
      }
    };

    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  /**
   * Mapeia nomes de categorias para IDs (gcid) quando as categorias são carregadas.
   */
  useEffect(() => {
    if (!formData || categories.length === 0) return;

    setFormData((prev) => {
      if (!prev) return null;

      let updated = prev;

      // Categoria principal
      if (prev.categoryId && !categories.some((c) => c.gcid === prev.categoryId)) {
        const match = categories.find((c) => c.category === prev.categoryId);
        if (match) {
          updated = {
            ...updated,
            categoryId: match.gcid,
            category: match.category,
          };
        }
      } else if (prev.category && !prev.categoryId) {
        const match = categories.find(
          (c) => c.category === prev.category || c.gcid === prev.category
        );
        if (match) {
          updated = {
            ...updated,
            categoryId: match.gcid,
            category: match.category,
          };
        }
      }

      // Categorias secundárias
      if (
        prev.secondaryCategories &&
        prev.secondaryCategories.length > 0 &&
        (!prev.secondaryCategoryIds || prev.secondaryCategoryIds.length === 0)
      ) {
        const ids = prev.secondaryCategories
          .map((name) => {
            const match = categories.find(
              (c) => c.category === name || c.gcid === name
            );
            return match?.gcid;
          })
          .filter((id): id is string => Boolean(id));

        if (ids.length > 0) {
          updated = {
            ...updated,
            secondaryCategoryIds: ids,
          };
        }
      }

      return updated === prev ? prev : updated;
    });
  }, [
    categories,
    formData?.category,
    formData?.categoryId,
    formData?.secondaryCategories,
    formData?.secondaryCategoryIds,
  ]);

  /**
   * Atualiza os valores dos campos do formulário.
   * Suporta campos aninhados usando notação de ponto (ex: "stats.views").
   * 
   * @param e - Evento de mudança do input
   */
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          [parent]: {
            ...(prev[parent as keyof Profile] as object),
            [child]: value,
          },
        };
      });
    } else {
      setFormData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          [name]: value,
        };
      });
    }
  };

  return {
    formData,
    setFormData,
    categories,
    loadingCategories,
    handleChange,
  };
};
